import { config } from '../config';
import {
  handleAddDiscoveryHypothesis,
  handleAddHypothesisAtHalftime,
  handleCaptureSetPiece,
  handleCloseSetPiece,
  handleConfirmLock,
  handleCreateMatch,
  handleCreatePlayer,
  handleCreateTeam,
  handleDeleteAnnotation,
  handleDeleteOurPlayer,
  handleDiscardCoachCardDraft,
  handleFinalizeCoachCard,
  handleIncrement,
  handleInstallSeedTemplates,
  handlePromoteHalftimeAdjustment,
  handlePromoteTalkPoints,
  handleAdjustBoundaries,
  handleResolveFlag,
  handleSaveBoardAsTemplate,
  handleSetStatusOverride,
  handleStartAudioObservation,
  handleStartCapture,
  handleStartMatchNoteAudio,
  handleSwapBoardTile,
  handleTransition,
  handleUndoIncrement,
  handleUpdateMatchSetup,
  handleUpdateSetting,
  handleUpsertCoachCardDraft,
  handleUpsertOurPlayer,
  handleWriteAnnotation,
  handleWriteMatchNote,
} from '../domain/commands';
import type {
  Command,
  CommandType,
  Decision,
  Effect,
  Postcondition,
  RowOp,
} from '../domain/commands';
import { isEvidenced } from '../domain/evidence';
import { replay } from '../domain/counts';
import { IN_MATCH_STATES, MATCH_STATE } from '../domain/machine';
import type { DomainErrorCode } from '../domain/result';
import { openBlob } from '../data/blobStore';
import { TABLE, write } from '../data/db';
import type { TableName } from '../data/db';
import type { CommitReceipt } from '../data/commit';
import { ARTIFACT_PHASE } from '../types';
import type { CapturePhase, DomainLogEntry } from '../types';
import {
  annotations,
  cascade,
  coachCards,
  flagResolutions,
  hypotheses,
  matchNotes,
  matches,
  observations,
  ourPlayers,
  outbox,
  profile,
  setPieces,
  settings,
  talkPoints,
  teams,
} from '../data/repos';
import { CASCADE_TABLES } from '../data/repos/cascade';
import { CommandRejectedError, StorageError } from './errors';
import { flush, log } from './logger';
import { assertClaimed, touchInTransaction } from './writerLock';

/**
 * **The command bus** — design §1.1, and the reason the requirements' universal negatives are
 * discharge-able at all.
 *
 * > Everything the requirements state as a universal negative — "no other mutation exists on any
 * > code path" (INV-1), "no acknowledgment is ever rendered for an uncommitted mutation" (SES-2),
 * > "no other automatic transitions exist" (INV-3), "never changed by increments" (INV-4) — is a
 * > claim about *all* code paths. Such claims cannot be discharged by tests, only by there being one
 * > path. That is what the command bus buys.
 *
 * ── SES-2, mechanically ──────────────────────────────────────────────────────
 *
 * `dispatch` never acknowledges anything itself. It hands `onCommitted` to `data/db.ts`'s `write()`,
 * which invokes it **synchronously inside the native `IDBTransaction`'s `complete` listener** — design
 * §5.1 step 9. Every other outcome (a rejected handler, a failed postcondition, an aborted
 * transaction, a commit event that never arrives) leaves that callback uncalled, because there is no
 * second call site. The negative is a property of the wiring, not of remembering to check a flag.
 *
 * **The trap this is threading.** Dexie's transaction promise resolves *before* the `complete` event
 * in a browser and *after* it under `fake-indexeddb`. Code that acknowledges off the promise is green
 * in Node and broken on every phone — and SES-2 is precisely "acknowledge strictly after commit".
 * U-3 built `write()` so the wrong thing is unreachable from outside `data/`; this file's only job is
 * to not invent a second one. `bus.test.ts` proves it the same way `commit.test.ts` does: by
 * suppressing the `complete` listener while leaving Dexie free to resolve, and asserting the ack
 * never fires.
 *
 * **A-1 is settled and passes** (commit p95 10 ms against a 40 ms budget; tap→ack p95 and p99 both
 * 37 ms against 100 ms), so design finding F-9 does not fire. SES-2 is held absolutely: there is no
 * optimistic path, no pending state, and no timeout branch that acknowledges anyway. §5.3's refusal
 * of a third visual state stands.
 *
 * ── One transaction per command ──────────────────────────────────────────────
 *
 * Each dispatch opens exactly one `readwrite` scope, declared per command type in {@link SCOPES}.
 * The scope is not a formality: Dexie throws on an operation against a table outside it, which is
 * what makes "exactly one transaction per Command" checkable rather than aspirational. The snapshot
 * is read **inside** that scope (§1.1), so there is no read-then-write race even under the
 * single-flight queue.
 */

const MODULE = 'bus';

/**
 * Filler for a snapshot whose match row has already vanished.
 *
 * The value is never consulted — `handleTransition` rejects with `ERR_MATCH_NOT_FOUND` before any
 * guard reads it, and no `Match.state` transition is gated on `capturePhase` in the first place
 * (§17.1). It exists because `TransitionFacts` requires every field: U-2 made them non-optional on
 * the grounds that "an optional guard fact is a guard that silently does not run".
 */
const DEFAULT_CAPTURE_PHASE: CapturePhase = 'discovery';

// ─────────────────────────────────────────────────────────────────────────────
// Result
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `ERR_STORAGE` wraps anything IndexedDB refused.
 *
 * There is deliberately **no** §13.7 code here. The re-decided §13.7 *"reports; it never refuses"*,
 * so a context that does not hold the writer lock dispatches exactly like one that does — the lock
 * produces a banner in `app/writerLock.ts`, never a rejection on this path.
 */
export type BusErrorCode = DomainErrorCode | 'ERR_STORAGE';

export type DispatchResult =
  | {
      readonly ok: true;
      /** `commitMs` is design §5.1's `ACK_COMMIT_BUDGET_MS` measurement — logged, never a UI branch. */
      readonly receipt: CommitReceipt;
    }
  | { readonly ok: false; readonly code: BusErrorCode; readonly message: string };

export interface DispatchOptions {
  /**
   * Design §5.1 step 9's acknowledgment point, and the only one.
   *
   * Invoked **synchronously inside** the `complete` listener, so LIVE-2's count flash and full-tile
   * pulse begin in the commit moment rather than a frame later. It is never called for a command
   * that did not commit.
   */
  readonly onCommitted?: (at: number) => void;
  /** Wall clock for the snapshot's `now`. Injected so SES-1's anchor window is testable. */
  readonly now?: () => number;
  /** Overrides the commit-event stall bound. Tests use it; production takes the config default. */
  readonly timeoutMs?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Transaction scopes, per command
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The tables each command's single transaction spans.
 *
 * `Increment` is the one that matters and is deliberately the shortest list design §5.1 permits:
 * *"One `readwrite` transaction over `['observations','hypotheses']` — exactly two rows, no blob, no
 * log write, no `matches` row."* It reads no `matches` row because §5.1 step 4 pre-builds the whole
 * Observation — including its derived match minute — at `pointerdown`, before the transaction opens.
 * Widening this list is how the hot path loses its budget, so it is declared here where a diff shows
 * it rather than assembled at the call site.
 */
const SCOPES: Record<CommandType, readonly TableName[]> = {
  Increment: [TABLE.observations, TABLE.hypotheses],
  /**
   * U-10 / LIVE-6. Wider than `Increment` by one table, deliberately: the undo window is bounded by
   * `Match.state` ("MUST NOT act once state leaves in-match states"), and a Full Time can commit
   * between the finger landing on the `−1` and this transaction opening. Reading `matches` here is
   * what makes that a property of the commit rather than of what the surface last rendered.
   *
   * It is not the hot path — §5.1's two-table budget argument is about the *tap*, which this is not.
   * An undo happens at most a handful of times a match and never in a burst.
   */
  UndoIncrement: [TABLE.observations, TABLE.hypotheses, TABLE.matches],
  SetStatusOverride: [TABLE.hypotheses],
  ConfirmLock: [TABLE.hypotheses, TABLE.matches],
  /**
   * U-13b / LOCK-6 / DD-3 — the swap. `matches` carries the halftime window and the `capturePhase`
   * check; `hypotheses` carries the real positions `planSwap` reasons over and the two writes.
   *
   * Both position writes are in **one** transaction because §3.4's `&[matchId+boardPosition]` unique
   * index rejects the incoming write while the outgoing row still holds the slot — the ordering
   * inside `planSwap` is what makes it legal, and a second transaction would expose a board with a
   * hole in it to any read that landed between them.
   */
  SwapBoardTile: [TABLE.hypotheses, TABLE.matches],
  /** U-13b / PRE-3 at halftime — the same two tables, and for the same reasons, as DISC-5's add. */
  AddHypothesisAtHalftime: [TABLE.hypotheses, TABLE.matches],
  /**
   * U-13c / FLAG-3 — the resolution, the promoted hypothesis, and the two rows they are checked
   * against.
   *
   * `observations` is in scope **read-only**, and that is the point: the handler reads the flag to
   * refuse a `voiceNote` id or a foreign match's row, and {@link FlagResolutionOp} cannot express any
   * write to it. So INV-1's *"the flag Observation is never mutated"* has the same two-layer defence
   * HT-6 has — the type makes a write uncompilable, and here the table is deliberately *present*, so
   * the compile-time half is carrying the guarantee alone.
   *
   * `flagResolutions` carries the append **and** the duplicate check, which must share a transaction
   * or two taps a few hundred milliseconds apart could both read "unresolved" and both write.
   */
  ResolveFlag: [TABLE.flagResolutions, TABLE.observations, TABLE.hypotheses, TABLE.matches],
  /**
   * U-14a / SES-8 — the anchors and **every** minute derived from them, in one scope.
   *
   * §10.1: *"re-derives every Observation and MatchNote minute in one transaction"*. The three tables
   * are therefore not a convenience: a match whose anchors moved but whose observation minutes did
   * not is a match on which INV-7's single temporal authority has two answers, and there is no
   * repair path because the old anchors are gone.
   */
  AdjustBoundaries: [TABLE.matches, TABLE.observations, TABLE.matchNotes],
  CreateMatch: [TABLE.matches, TABLE.activeMatch],
  // U-7 — Pre-Match setup. Each scope is the smallest one its snapshot and ops need; `CreateTeam`
  // reads nothing, and `UpdateMatchSetup` reads `teams` only to refuse a setup pointed at an
  // opponent that no longer exists.
  CreateTeam: [TABLE.teams],
  InstallSeedTemplates: [TABLE.templates],
  /**
   * U-16 / PRE-6 — `templates` is written; `matches` and `hypotheses` are in scope **read-only**.
   *
   * The same shape as `PromoteHalftimeAdjustment`'s: the handler needs the match (for §6.0's
   * post-match window) and the board (for the entries), and {@link TemplateOp} cannot express a
   * write to either. So *"saving a template changes nothing about the match it was saved from"* has
   * the two-layer defence this codebase uses for every read-only table in a scope — except that
   * here the compile-time half carries it alone, which is why the op type is a one-line `Extract`
   * rather than a reuse of `SetupOp`.
   */
  SaveBoardAsTemplate: [TABLE.templates, TABLE.matches, TABLE.hypotheses],
  CreatePlayer: [TABLE.players],
  /**
   * B-2 / spec §4.2 — the roster commands read and write exactly one table.
   *
   * No `matches`: the roster is profile-scoped with no match owner (B-1), the window is "any"
   * by ruling, so there is no state for a wider scope to read — the B-1 cascade lesson cuts both
   * ways, and a table in a scope that nothing consults is the inverse defect.
   */
  UpsertOurPlayer: [TABLE.ourPlayers],
  DeleteOurPlayer: [TABLE.ourPlayers],
  /**
   * B-2 / spec §4.2 — the annotation write. `matches` carries the firstHalf→distill window;
   * `hypotheses` is in scope **read-only** for the insert arm's attachment check, and
   * {@link AnnotationOp} cannot express a write to it — the same two-layer defence
   * `PromoteHalftimeAdjustment` documents. `observations` is absent from scope AND op set:
   * an annotation is commentary, not evidence, and has no path to a count.
   */
  WriteAnnotation: [TABLE.annotations, TABLE.matches, TABLE.hypotheses],
  /** B-2 — the delete needs the window and the target; no attachment question, no `hypotheses`. */
  DeleteAnnotation: [TABLE.annotations, TABLE.matches],
  /**
   * B-3 / spec §4.2 — the draft write. `matches` carries the context's window and the
   * scenario-20 purpose check; `hypotheses` is in scope **read-only** for the `ERR_CARD_SOURCE`
   * membership test, and {@link CoachCardOp} cannot express a write to it — the two-layer
   * defence `WriteAnnotation` documents, one workflow over. `talkPoints` is absent from scope
   * AND op set: the card workflow replaces the frozen distillation output and must not be able
   * to reach it.
   */
  UpsertCoachCardDraft: [TABLE.coachCards, TABLE.matches, TABLE.hypotheses],
  /** B-3 — finalize needs the window and the stored row; no source question, no `hypotheses`. */
  FinalizeCoachCard: [TABLE.coachCards, TABLE.matches],
  /** B-3 — the discard, same two questions as finalize. */
  DiscardCoachCardDraft: [TABLE.coachCards, TABLE.matches],
  /**
   * B-4 / spec §4.2 (F-5) — the settings write reads and writes exactly one table.
   *
   * No `matches`: the window is "any" by ruling — settings are device/profile data with no
   * match owner, the `UpsertOurPlayer` argument — and a table in a scope that nothing consults
   * is the inverse of the B-1 cascade defect.
   */
  UpdateSetting: [TABLE.settings],
  UpdateMatchSetup: [TABLE.matches, TABLE.teams],
  StartCapture: [TABLE.hypotheses, TABLE.matches],
  // U-8 — DISC-5. `matches` carries the phase/state window; `hypotheses` carries the id check and
  // the insert. Not the tap path, so the extra table costs nothing NFR-1 is measuring.
  AddDiscoveryHypothesis: [TABLE.hypotheses, TABLE.matches],
  /**
   * U-11 / design §8.2 step 1 — the Observation and its (empty) blob row, together.
   *
   * `audioBlobs` is in scope but **no bytes cross it**: `openAudioBlob` writes a row with
   * `blob: null` and `chunks: []`. §5.1's *"exactly two rows, no blob"* budget is about the
   * `Increment` tap and is untouched; this is a hold or a Flag-bar press, which happens a handful of
   * times a half and never in a burst.
   *
   * The two writes must share a transaction or §8.2's ordering breaks: the recorder starts *after*
   * this commits, so a partially-applied start would leave `MediaRecorder` emitting chunks toward an
   * `audioBlobId` with no row, and `appendChunk` refuses those by design (a silently dropped chunk
   * is a hole nothing downstream can detect — NFR-8).
   */
  StartAudioObservation: [TABLE.observations, TABLE.hypotheses, TABLE.matches, TABLE.audioBlobs],
  /**
   * U-12 / §9 — *"a small, non-hot-path transaction"*, written on every marker tap.
   *
   * Two tables, and `matches` is read only: SP-1's window is `isInMatch`, and the state can leave it
   * between the finger landing on the diagram and this transaction opening. `hypotheses` and
   * `observations` are **absent from the scope as well as from the op set** — Dexie throws on an
   * operation against a table outside the declared scope, so SP-1's *"disturbs no tile position"* has
   * a second, runtime mechanism underneath the compile-time one ({@link SetPieceOp}).
   */
  CaptureSetPiece: [TABLE.setPieces, TABLE.matches],
  /** U-12 / §9 — clearing `draft` reads and writes exactly one row. See `CloseSetPieceCommand`. */
  CloseSetPiece: [TABLE.setPieces],
  /**
   * U-13 / HT-5 — and **`hypotheses` is in scope read-only**, which is the point.
   *
   * The handler reads it to refuse a `sourceHypothesisId` that no longer belongs to the match, and
   * `HalftimeAdjustmentOp` cannot express a write to it. So HT-6's *"MUST NOT alter counts,
   * statuses, board positions or render order"* has the same two-layer defence SP-1 has: the type
   * makes a write uncompilable, and Dexie's scope makes an out-of-scope write throw — but here the
   * table is deliberately *present*, so the compile-time half is carrying the guarantee alone and is
   * where the argument has to be made. See {@link HalftimeAdjustmentOp}.
   */
  PromoteHalftimeAdjustment: [TABLE.talkPoints, TABLE.matches, TABLE.hypotheses],
  /**
   * U-14b / DIST-2 — the **same three tables** `PromoteHalftimeAdjustment` declares, and the same
   * argument holds unchanged: `hypotheses` is present **read-only** so the handler can refuse a
   * dangling `sourceHypothesisId` (DIST-6) and count the match's patterns for DIST-7's gate, while
   * `DistillationOp` cannot express a write to it. Distillation touches no count, no status and no
   * board position, and the compile-time half is what carries that.
   *
   * `observations` and `flagResolutions` are absent: DIST-1's queue is closed before `distill` is
   * reachable, and nothing on this path may look at, let alone write, an Observation.
   */
  PromoteTalkPoints: [TABLE.talkPoints, TABLE.matches, TABLE.hypotheses],
  /**
   * U-13 / HT-8 — INV-14's table plus the `halftime` state gate.
   *
   * `hypotheses` and `observations` are absent from the scope **and** from `MatchNoteOp`, so
   * INV-14's *"never affects `cachedCount`, `status`, or any derived value"* is enforced twice over.
   * No `audioBlobs`: the bytes are written by the recorder outside any command (§8.2), and this
   * command carries only the id they will land under.
   */
  WriteMatchNote: [TABLE.matchNotes, TABLE.matches],
  /**
   * U-13c / HT-8's voice — `WriteMatchNote`'s two tables **plus** the empty blob row.
   *
   * The extra table is the whole difference between the two commands, and it is here rather than on
   * `WriteMatchNote` for the reason that scope's own note gives: a plain text save must not be able
   * to reach `audioBlobs` at all. Still no bytes cross it — `openAudioBlob` writes `blob: null`,
   * `chunks: []`, exactly as `StartAudioObservation` does — and the two writes must share a
   * transaction or §8.2's ordering breaks: the recorder starts *after* this commits, so a partially
   * applied start would leave `MediaRecorder` emitting chunks toward an `audioBlobId` with no row,
   * and `appendChunk` refuses those by design.
   *
   * `hypotheses` and `observations` stay absent from both the scope and {@link MatchNoteAudioOp}, so
   * INV-14 keeps the same two-layer defence on the voice path it has on the text one.
   */
  StartMatchNoteAudio: [TABLE.matchNotes, TABLE.matches, TABLE.audioBlobs],
  /**
   * Transitions are rare (§13.6 says so explicitly) and read wide: SES-9's exclusivity guard needs
   * every match's state, DIST-1's needs the unresolved flags, INV-6's needs the talk points,
   * §13.6's drift alarm needs the whole ledger, and §2.2 writes the outbox row and the `activeMatch`
   * pointer in this same transaction (ENV-9).
   *
   * **`CASCADE_TABLES` is spread rather than re-listed.** `DiscardSetup` runs INV-8's cascade in this
   * transaction, and a cascade spans `teams` and `players` as well. Hand-listing the tables here
   * left both out, and the failure was a `NotFoundError` deep inside `applyCascade` — a rolled-back
   * `DiscardSetup` that looked like a storage fault rather than a scope bug. Deriving the union means
   * a table added to the cascade cannot silently fall out of the scope that has to carry it.
   */
  Transition: [
    ...new Set<TableName>([
      ...CASCADE_TABLES,
      TABLE.matches,
      TABLE.hypotheses,
      TABLE.observations,
      TABLE.flagResolutions,
      TABLE.talkPoints,
      // B §6 / F-1 — `CompleteMatch`'s draft-card guard reads this table (read-only: no
      // `TransitionOp` can express a `coachCards` write, so the two-layer defence holds).
      TABLE.coachCards,
      TABLE.outbox,
      TABLE.activeMatch,
    ]),
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// The single-flight queue (§5.2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Design §5.2: *"Taps 1..N enqueue N commands, drained serially. The ack for tap k renders when tap
 * k commits, so under very rapid tapping the visible count trails the user's finger by the queue
 * depth. This is honest — the number shown is the number committed — and it is what SES-2 requires."*
 *
 * Serialization is also what makes the in-transaction snapshot sufficient for INV-2: two overlapping
 * `readwrite` transactions could each read the same `cachedCount` and each write `prev+1`, losing an
 * increment with both transactions reporting success.
 */
let tail: Promise<unknown> = Promise.resolve();

function enqueue<T>(job: () => Promise<T>): Promise<T> {
  // `.then(job, job)` runs the next command whether the previous settled or threw: one rejected
  // command must not poison every subsequent tap in the half.
  const run = tail.then(job, job);
  tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** Test seam — lets a suite start from an empty queue rather than inheriting the previous file's. */
export function __resetBusForTests(): void {
  tail = Promise.resolve();
}

// ─────────────────────────────────────────────────────────────────────────────
// dispatch
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validates, applies and commits one command, then acknowledges.
 *
 * @returns `{ok:true, receipt}` **only** after the commit event. `{ok:false, code}` for every
 *   rejection — SES-1 requires illegal transitions to be "rejected in the domain layer, not merely
 *   hidden in the UI", and requires that rejection to be observable; the code is in the type of
 *   every call site and the OBS-1 entry is written before this returns.
 */
export function dispatch(cmd: Command, options: DispatchOptions = {}): Promise<DispatchResult> {
  return enqueue(() => runCommand(cmd, options));
}

async function runCommand(cmd: Command, options: DispatchOptions): Promise<DispatchResult> {
  // A context that never claimed never requested the Web Lock, so §13.7 is absent rather than
  // satisfied — no second window could be detected and no banner could appear. Reported once, at
  // `error`, never refused: see `writerLock.assertClaimed`.
  //
  // Nothing else here consults the writer lock. §13.7 was re-decided to report and not refuse, and
  // a `!holdsLock` guard on this path is exactly the defect that was withdrawn — U-8's first
  // browser run dispatched every tap, committed nothing, and blamed a window that did not exist.
  assertClaimed();

  const now = options.now ?? (() => Date.now());
  const pending: {
    lines: DomainLogEntry[];
    drift: DriftReport | null;
    movedState: boolean;
  } = { lines: [], drift: null, movedState: false };

  try {
    const result = await write(
      SCOPES[cmd.type],
      async () => {
        const decision = await decide(cmd, now());
        pending.lines = [...decision.log, ...(decision.ok ? decision.value.log : [])];

        // A rejection aborts. Nothing has been applied at this point, so the abort protects nothing
        // that was written — it protects the *acknowledgment*: an aborted transaction never fires
        // `complete`, so `onCommitted` cannot run for a command the domain refused (SES-2).
        if (!decision.ok) throw new CommandRejectedError(decision.code, decision.message);

        const effect = decision.value;
        // §12.1a's fourth flush trigger, decided here and acted on after the commit. Read off the
        // **ops**, not off `cmd.type`: `transitionMatch` is the only op in the algebra that can
        // write `Match.state` (`TransitionRowPatch.state` is required; `MatchSetupPatch` cannot
        // express it and `insertMatch` creates rather than transitions), so any command that moves
        // the state — present or future, whatever it is called — flushes without a second list to
        // keep in step. A `cmd.type === 'Transition'` test would have been that second list.
        pending.movedState = effect.ops.some((op) => op.t === 'transitionMatch');
        await applyOps(effect.ops, now());
        if (effect.outbox) await outbox.enqueue(effect.outbox);
        await runPostconditions(effect.post);
        pending.drift = effect.driftCheck ? await checkDrift(effect.driftCheck.matchId) : null;
      },
      { onCommitted: options.onCommitted, timeoutMs: options.timeoutMs, label: cmd.type },
    );

    emit(pending.lines);
    reportDrift(pending.drift);
    /**
     * OBS-1 / §12.1a — *"every `Match.state` transition → flush now"*, the trigger this section has
     * claimed since the first draft and that had never been built.
     *
     * **After the commit and after `emit`, never inside the transaction.** §12.1 forbids the logger
     * writing inside a command's transaction, and the lines this flush is here to persist are the
     * ones `emit` has only just buffered — flushing before them would drain the previous state's log
     * and leave the transition itself unwritten, which inverts the point of correlating the two.
     *
     * Fire-and-forget: `flush()` never rejects (the single sanctioned NFR-8 exception), and a
     * transition must not wait on an observability write. Transitions are rare by construction
     * (§13.6 says so), so this adds no write pressure to any hot path.
     */
    if (pending.movedState) void flush();
    if (result.receipt.commitMs > config.ACK_COMMIT_BUDGET_MS) {
      // §5.3 / §13 — a *logged measurement*, never a UI branch. The slow commit is counted into the
      // p95 the archive carries; the user sees a pressed tile and then a flash, and nothing else.
      log.warn(MODULE, 'commit exceeded the acknowledgment budget', {
        type: cmd.type,
        commitMs: result.receipt.commitMs,
        budgetMs: config.ACK_COMMIT_BUDGET_MS,
      });
    }
    return { ok: true, receipt: result.receipt };
  } catch (cause) {
    // The domain's own log lines were built before the throw and describe why it happened; emitting
    // them here keeps a rejection observable even though the transaction that carried them aborted.
    emit(pending.lines);
    if (cause instanceof CommandRejectedError) {
      // The class types `code` as `string` so that `app/errors.ts` — which `archive/` imports under
      // §1.2's one carve-out — stays free of any reference to `domain/`. The value came from a
      // `DomainErrorCode` three lines up in `runCommand`; this is the single site that knows it.
      return { ok: false, code: cause.code as DomainErrorCode, message: cause.message };
    }
    if (cause instanceof StorageError) {
      log.error(MODULE, 'command failed and was rolled back', {
        type: cmd.type,
        error: cause.toJSON(),
      });
      return { ok: false, code: 'ERR_STORAGE', message: cause.message };
    }
    throw cause;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot + handler
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reads the snapshot **inside** the transaction and runs the pure handler (§1.1).
 *
 * Each branch loads only what its handler's guards consult. That is not micro-optimization on the
 * hot path — it is the difference between `Increment` touching two tables and touching ten, which is
 * the whole of §5.1's budget argument.
 */
async function decide(cmd: Command, now: number): Promise<Decision> {
  switch (cmd.type) {
    case 'Increment':
      return handleIncrement(cmd, { now, hypothesis: await hypotheses.getHypothesis(cmd.hypothesisId) });

    case 'UndoIncrement':
      // Three reads, all inside the transaction (§1.1). `listUndoCandidates` queries the
      // `[matchId+hypothesisId+kind+createdAt]` range with `kind` pinned, so the candidate set
      // structurally cannot contain a `voiceNote`, a `flag`, or another hypothesis's rows (§3.4).
      return handleUndoIncrement(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        hypothesis: await hypotheses.getHypothesis(cmd.hypothesisId),
        candidates: await observations.listUndoCandidates(cmd.matchId, cmd.hypothesisId),
      });

    case 'SetStatusOverride':
      return handleSetStatusOverride(cmd, {
        now,
        hypothesis: await hypotheses.getHypothesis(cmd.hypothesisId),
      });

    case 'ConfirmLock':
      return handleConfirmLock(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        hypotheses: await hypotheses.listHypothesesForMatch(cmd.matchId),
      });

    case 'SwapBoardTile':
      // Whole rows, not ids: the tail index and the outgoing tile's exact slot are facts about the
      // committed board, and `planSwap`/`planFillEmptySlot` compute both from the real positions.
      return handleSwapBoardTile(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        hypotheses: await hypotheses.listHypothesesForMatch(cmd.matchId),
      });

    case 'AddHypothesisAtHalftime':
      return handleAddHypothesisAtHalftime(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existingIds: (await hypotheses.listHypothesesForMatch(cmd.matchId)).map((row) => row.id),
      });

    case 'CreateMatch':
      return handleCreateMatch(cmd, { now, facts: await exclusivityFacts(cmd.row.id) });

    case 'CreateTeam':
      return handleCreateTeam(cmd, { now });

    case 'InstallSeedTemplates':
      return handleInstallSeedTemplates(cmd, {
        now,
        existingIds: (await profile.listTemplates()).map((row) => row.id),
      });

    case 'SaveBoardAsTemplate':
      return handleSaveBoardAsTemplate(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // Every hypothesis, not the positioned ones: PRE-6's "positioned" filter is
        // `templateEntriesFromBoard`'s, and a second filter here would be a second answer.
        hypotheses: await hypotheses.listHypothesesForMatch(cmd.matchId),
        existingTemplateIds: (await profile.listTemplates()).map((row) => row.id),
      });

    case 'CreatePlayer':
      return handleCreatePlayer(cmd, {
        now,
        existing: await teams.findPlayerByNumber(cmd.row.teamId, cmd.row.number),
      });

    case 'UpsertOurPlayer':
      // One read: the insert-vs-edit decision (INV-14's discipline) is made against the row the
      // transaction sees, never against what the My Team sheet last rendered.
      return handleUpsertOurPlayer(cmd, {
        now,
        existing: await ourPlayers.getOurPlayer(cmd.row.id),
      });

    case 'DeleteOurPlayer':
      return handleDeleteOurPlayer(cmd, {
        now,
        existing: await ourPlayers.getOurPlayer(cmd.playerId),
      });

    case 'WriteAnnotation':
      return handleWriteAnnotation(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // Consulted by the insert arm's attachment check; the edit arm's row already proved its
        // hypothesis by surviving beside it under the INV-8 cascade.
        hypothesis: await hypotheses.getHypothesis(cmd.row.hypothesisId),
        // INV-14's discipline: an id already on disk is an edit, decided here, never at the sheet.
        existing: await annotations.getAnnotation(cmd.row.id),
      });

    case 'DeleteAnnotation':
      return handleDeleteAnnotation(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existing: await annotations.getAnnotation(cmd.annotationId),
      });

    case 'UpsertCoachCardDraft':
      // Four reads, all inside the transaction (§1.1) — see the snapshot type for what each
      // refuses. Ids only for the hypotheses: `ERR_CARD_SOURCE` is a set-membership question,
      // the `PromoteHalftimeAdjustment` argument.
      return handleUpsertCoachCardDraft(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existingForContext: await coachCards.getForMatchContext(cmd.matchId, cmd.row.context),
        existingById: await coachCards.getCoachCard(cmd.row.id),
        hypothesisIds: (await hypotheses.listHypothesesForMatch(cmd.matchId)).map((row) => row.id),
      });

    case 'FinalizeCoachCard':
      return handleFinalizeCoachCard(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existing: await coachCards.getCoachCard(cmd.cardId),
      });

    case 'DiscardCoachCardDraft':
      return handleDiscardCoachCardDraft(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existing: await coachCards.getCoachCard(cmd.cardId),
      });

    case 'UpdateSetting':
      // One read: created-vs-replaced for the OBS-1 line — a fact about committed state, never
      // about what a preferences screen last rendered. The key may be an untrusted string at
      // runtime; `getSetting` answers `undefined` for one the handler is about to refuse.
      return handleUpdateSetting(cmd, {
        now,
        existing: await settings.getSetting(cmd.key),
      });

    case 'UpdateMatchSetup':
      return handleUpdateMatchSetup(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // Only asked when the patch moves the match to another opponent; an unconditional read
        // would put a `teams` lookup on every stepper tick for a fact nothing consults.
        teamExists:
          cmd.patch.teamId === undefined
            ? true
            : (await teams.getTeam(cmd.patch.teamId)) !== undefined,
      });

    case 'StartCapture':
      return handleStartCapture(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        hypotheses: await hypotheses.listHypothesesForMatch(cmd.matchId),
      });

    case 'AddDiscoveryHypothesis':
      return handleAddDiscoveryHypothesis(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existingIds: (await hypotheses.listHypothesesForMatch(cmd.matchId)).map((row) => row.id),
      });

    case 'StartAudioObservation':
      return handleStartAudioObservation(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // Only read for a voice note. A flag has no hypothesis by definition (FLAG-2), and an
        // unconditional lookup would put a `hypotheses` read on the Flag bar's path for a row the
        // handler is about to refuse to accept.
        hypothesis:
          cmd.observation.hypothesisId === null
            ? undefined
            : await hypotheses.getHypothesis(cmd.observation.hypothesisId),
      });

    case 'CaptureSetPiece':
      return handleCaptureSetPiece(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // Read inside the transaction, because everything this command refuses is a comparison
        // against what is stored: a closed capture, or SP-4's minute moved after creation.
        existing: await setPieces.getSetPiece(cmd.row.id),
      });

    case 'CloseSetPiece':
      return handleCloseSetPiece(cmd, { now, existing: await setPieces.getSetPiece(cmd.setPieceId) });

    case 'PromoteHalftimeAdjustment':
      // Ids only, not rows: the guard is "does this hypothesis still belong to this match", and a
      // whole-row read would pull the entire hypothesis list into the transaction for a set
      // membership test — the same argument `AddDiscoveryHypothesis` makes one branch up.
      return handlePromoteHalftimeAdjustment(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        hypothesisIds: (await hypotheses.listHypothesesForMatch(cmd.matchId)).map((row) => row.id),
        existingHalftime: await talkPoints.listForMatch(cmd.matchId, ARTIFACT_PHASE.halftime),
      });

    case 'PromoteTalkPoints': {
      /**
       * **Whole rows here, and only here among the three id-only snapshots** — DIST-9 (revision 4)
       * is why, and the cost is stated rather than absorbed.
       *
       * DIST-7's gate used to be *"how many hypotheses exist for a match"*, which is an array
       * length; DIST-9 re-bases it on how many the match produced **evidence** for, which is a
       * predicate over two committed columns. There is no id-shaped read that answers it, and a
       * count the surface computed a tap ago is exactly what reading inside the transaction
       * exists to avoid — an increment committed between render and tap must move this gate.
       *
       * The read is the same **one** `listHypothesesForMatch` the id-only form already made, so
       * the transaction gains no round trip; it keeps two columns of each row instead of one.
       * This command writes at most three talk points and runs once per match, off every latency
       * budget in the product (NFR-1 binds in-play capture).
       */
      const rows = await hypotheses.listHypothesesForMatch(cmd.matchId);
      return handlePromoteTalkPoints(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        hypothesisIds: rows.map((row) => row.id),
        evidencedHypothesisIds: rows.filter(isEvidenced).map((row) => row.id),
        existingFullTime: await talkPoints.listForMatch(cmd.matchId, ARTIFACT_PHASE.fullTime),
      });
    }

    case 'WriteMatchNote':
      return handleWriteMatchNote(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // INV-14's mutability, read inside the transaction: an id already on disk is an edit, and
        // deciding that at the call site would race the user's own second save.
        existing: await matchNotes.getNote(cmd.row.id),
      });

    case 'StartMatchNoteAudio':
      // The same two reads as `WriteMatchNote`, because the voice start answers the same two
      // questions. Reading `existing` inside the transaction is what stops a record press that
      // raced the user's own *Done* from turning an edit into a duplicate-key abort.
      return handleStartMatchNoteAudio(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        existing: await matchNotes.getNote(cmd.row.id),
      });

    case 'ResolveFlag':
      return handleResolveFlag(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        // The flag itself, not a copy the surface carried: the id crossed a UI boundary and
        // everything the handler refuses is a fact about the stored row (kind, owning match).
        flag: await observations.getObservation(cmd.observationId),
        // Read inside the transaction so two taps a few hundred milliseconds apart cannot both see
        // "unresolved". The unique index is the backstop; this is the renderable answer.
        alreadyResolved:
          (await flagResolutions.getResolutionForObservation(cmd.observationId)) !== undefined,
        // Ids only: both arms ask a set-membership question — *attach* that the id IS present,
        // *create* that it is NOT — and whole rows would pull the match's hypothesis list into a
        // transaction that writes at most one.
        existingHypothesisIds: (await hypotheses.listHypothesesForMatch(cmd.matchId)).map(
          (row) => row.id,
        ),
      });

    case 'AdjustBoundaries': {
      // SES-8 — the rows, not counts. The handler computes every new minute through
      // `clock.rederiveMatchMinute` (§2.4's single function) and emits them, so `data/` never
      // derives a minute and the screen's preview is the same call.
      const [rows, notes] = await Promise.all([
        observations.listObservationsForMatch(cmd.matchId),
        matchNotes.listForMatch(cmd.matchId),
      ]);
      return handleAdjustBoundaries(cmd, {
        now,
        match: await matches.getMatch(cmd.matchId),
        observations: rows.map((row) => ({
          id: row.id,
          createdAt: row.createdAt,
          matchMinute: row.matchMinute,
        })),
        notes: notes.map((row) => ({
          id: row.id,
          createdAt: row.createdAt,
          matchMinute: row.matchMinute,
        })),
      });
    }

    case 'Transition': {
      const match = await matches.getMatch(cmd.matchId);
      return handleTransition(cmd, {
        now,
        match,
        facts: {
          ...(await exclusivityFacts(cmd.matchId)),
          halfLengthMin: match?.halfLengthMin ?? 0,
          unresolvedFlagCount: (await flagResolutions.listUnresolvedFlags(cmd.matchId)).length,
          // B §6 / F-1 — `CompleteMatch`'s second guard, read inside the transaction like every
          // other fact here: a draft committed by another surface between the record's render and
          // this dispatch must still block, which is exactly the race the guard exists for.
          draftCardCount: (await coachCards.listForMatch(cmd.matchId)).filter(
            (row) => row.status === 'draft',
          ).length,
          // `ARTIFACT_PHASE.fullTime`, never the bare string: `'fullTime'` is also a `MatchState`,
          // and the `ses-1/match-state-literals` rule keeps its full strength in `app/` because
          // types/ offers this named map (see the note on `ArtifactPhase`).
          fullTimeTalkPointRanks: (
            await talkPoints.listForMatch(cmd.matchId, ARTIFACT_PHASE.fullTime)
          ).map((row) => row.rank),
          abandoned: match?.abandoned ?? false,
          capturePhase: match?.capturePhase ?? DEFAULT_CAPTURE_PHASE,
        },
      });
    }
  }
}

/**
 * SES-9 and §2.2's two exclusivity facts.
 *
 * Both exclude the subject match itself. SES-9 restricts *other* matches — a match already in
 * `halftime` must not block its own Kickoff 2H — and §2.2's `preMatch` rule is about a *second*
 * unstarted setup, so a match creating itself is not competing with itself.
 */
async function exclusivityFacts(
  selfMatchId: string,
): Promise<{ anotherMatchIsInMatch: boolean; anotherPreMatchExists: boolean }> {
  const inMatch = await matches.listMatchesByState(IN_MATCH_STATES);
  const preMatch = await matches.listMatchesByState([MATCH_STATE.preMatch]);
  return {
    anotherMatchIsInMatch: inMatch.some((row) => row.id !== selfMatchId),
    anotherPreMatchExists: preMatch.some((row) => row.id !== selfMatchId),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The applier — one op, one repo call
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Applies the op list **in order**, inside the caller's transaction.
 *
 * Order is load-bearing, not incidental: `planSwap` nulls the outgoing `boardPosition` first because
 * IndexedDB enforces the unique `&[matchId+boardPosition]` index per `put`, so reordering these
 * writes "for tidiness" breaks every board swap (§3.4).
 *
 * Every branch delegates to a U-3 repo rather than touching a table, so each repo's own
 * in-transaction guard stays on this path: `insertObservation`'s INV-10 minute check,
 * `deleteIncrement`'s re-read that refuses a `voiceNote`/`flag` or a foreign hypothesis,
 * `writePositions`' `BOARD_CAP` abort, `confirmLock`'s refusal of a second lock. The bus is a
 * dispatcher over the algebra; it is never a second writer.
 */
async function applyOps(ops: readonly RowOp[], now: number): Promise<void> {
  for (const op of ops) {
    switch (op.t) {
      case 'insertMatch':
        await matches.insertMatch(op.row);
        break;
      case 'transitionMatch':
        await matches.applyTransition(op.id, op.patch);
        break;
      case 'writeTriagePassed':
        await matches.writeTriagePassed(op.id, op.ts);
        break;
      case 'patchMatchSetup':
        await matches.updateSetup(op.id, op.patch);
        break;
      case 'insertTeam':
        await teams.insertTeam(op.row);
        break;
      case 'insertTemplate':
        await profile.insertTemplate(op.row);
        break;
      case 'insertPlayer':
        await teams.insertPlayer(op.row);
        break;
      case 'confirmLock':
        await matches.confirmLock(op.id, op.lockTs);
        break;
      case 'cascadeDeleteMatch':
        // INV-8 — `applyCascade` re-plans inside this transaction and aborts if anything drifted.
        await cascade.applyCascade(await cascade.planCascade({ kind: 'match', id: op.matchId }));
        break;
      case 'insertHypothesis':
        await hypotheses.insertHypothesis(op.row);
        break;
      case 'writeCountState':
        await hypotheses.writeCountState(op.id, op.next);
        break;
      case 'setBoardPositions':
        await hypotheses.writePositions(op.matchId, op.writes);
        break;
      case 'insertObservation':
        await observations.insertObservation(op.row);
        break;
      case 'openAudioBlob':
        // §8.2 step 1. `blobStore.openBlob` guards on `requireTransaction`, so it joins this one
        // rather than opening its own — which is what makes the Observation and its blob row a
        // single atomic start.
        await openBlob(op.id, op.createdAt);
        break;
      case 'deleteIncrement':
        await observations.deleteIncrement({
          matchId: op.matchId,
          hypothesisId: op.hypothesisId,
          observationId: op.observationId,
        });
        break;
      case 'upsertSetPiece':
        // §11.7 — `upsertSetPiece` clamps and rounds the geometry at the repo boundary, which is
        // where INV-12's byte-determinism is made reachable. The bus does not round a second time.
        await setPieces.upsertSetPiece(op.row);
        break;
      case 'closeSetPieceDraft':
        await setPieces.clearDraft(op.id);
        break;
      case 'writeTalkPointSelection':
        // §10.2 — `writeSelection` deletes-then-rewrites `1..n` in this transaction. The delete-first
        // ordering is not tidiness: `&[matchId+phase+rank]` is unique and IndexedDB enforces it per
        // `put`, so re-ranking in place collides exactly as a board swap does (§3.4). It also runs
        // INV-6's `TALKPOINT_COUNT` bound and INV-13's discrimination check on every entry.
        await talkPoints.writeSelection(op.matchId, op.phase, op.ordered);
        break;
      case 'insertFlagResolution':
        // `repos/flagResolutions.insertResolution` uses `add`, not `put`: the unique `&observationId`
        // index turns a double-resolve into an aborted transaction rather than quietly overwriting
        // the first resolution on an append-only table (DIST-1).
        await flagResolutions.insertResolution(op.row);
        break;
      case 'insertMatchNote':
        await matchNotes.insertNote(op.row);
        break;
      case 'updateMatchNote':
        await matchNotes.updateNote(op.id, op.changes, now);
        break;
      case 'writeMatchAnchors':
        // SES-8. `writeAnchors` deliberately does not re-derive; the two ops below do, in this same
        // transaction, from minutes the handler already computed (§2.4).
        await matches.writeAnchors(op.id, op.anchors);
        break;
      case 'rederiveObservationMinutes':
        // INV-1's second sanctioned mutation. `rederiveMinutes` re-reads each row inside the
        // transaction and rewrites `matchMinute` alone, refusing a non-integer or negative (INV-10).
        await observations.rederiveMinutes(op.updates);
        break;
      case 'rederiveNoteMinutes':
        await matchNotes.rederiveMinutes(op.updates);
        break;
      case 'insertOurPlayer':
        await ourPlayers.insertOurPlayer(op.row);
        break;
      case 'updateOurPlayer':
        // B-2 — the repo stamps `updatedAt` from the bus's clock, the `updateMatchNote` shape.
        await ourPlayers.updateOurPlayer(op.id, op.changes, now);
        break;
      case 'deleteOurPlayer':
        await ourPlayers.deleteOurPlayer(op.id);
        break;
      case 'insertAnnotation':
        await annotations.insertAnnotation(op.row);
        break;
      case 'updateAnnotation':
        // B-2 — text alone; the repo stamps `updatedAt` from the bus's clock (INV-14's shape).
        await annotations.updateAnnotation(op.id, op.changes, now);
        break;
      case 'deleteAnnotation':
        await annotations.deleteAnnotation(op.id);
        break;
      case 'upsertCoachCardDraft':
        await coachCards.upsertCoachCardDraft(op.row);
        break;
      case 'finalizeCoachCard':
        // `finalizedAt` comes from the bus's clock at apply time, like every `updatedAt` stamp —
        // never from a render that may be a tap out of date.
        await coachCards.finalizeCoachCard(op.id, now);
        break;
      case 'deleteCoachCardDraft':
        await coachCards.deleteCoachCardDraft(op.id);
        break;
      case 'putSetting':
        // B-4 — create-or-replace; the handler has already validated the key against the closed
        // `SETTING_KEYS` union, so what reaches storage is a dispositioned key by construction.
        await settings.putSetting(op.row);
        break;
      case 'setActiveMatch':
        await touchInTransaction(now, op.matchId);
        break;
      case 'touchWriterHeartbeat':
        await touchInTransaction(now, undefined);
        break;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Postconditions (§1.1) — aborting, by definition
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs each assertion inside the transaction, after the ops.
 *
 * A violation throws, the transaction aborts, **nothing commits**, and therefore no `complete` event
 * fires and no acknowledgment renders (SES-2). §13's failure table names exactly this behavior for a
 * 9th positioned tile. There is no partial-write path to audit because there is no path on which
 * some ops survive an aborted transaction.
 *
 * @throws {StorageError} on violation.
 */
async function runPostconditions(post: readonly Postcondition[]): Promise<void> {
  for (const check of post) {
    switch (check.t) {
      case 'boardCap': {
        // INV-5 / LIVE-8. `writePositions` asserts this too; asserting it again at the bus level is
        // what covers a future op that writes a position without going through that repo function.
        const filled = await hypotheses.countPositioned(check.matchId);
        if (filled > config.BOARD_CAP) {
          throw new StorageError(
            `INV-5: match ${check.matchId} would hold ${filled} positioned tiles; the cap is ${config.BOARD_CAP}.`,
            { context: { matchId: check.matchId, filled, cap: config.BOARD_CAP } },
          );
        }
        break;
      }
      case 'talkPointSet': {
        // INV-6. Design §4: an invariant enforced only by a UI (DIST-2) is not an invariant.
        const match = await matches.getMatch(check.matchId);
        if (match === undefined) {
          throw new StorageError(
            `INV-6: match ${check.matchId} vanished before its talk-point set could be asserted.`,
            { context: { matchId: check.matchId } },
          );
        }
        await talkPoints.assertInv6(match);
        break;
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// §13.6 — the INV-2 drift alarm. Reports; never aborts, never auto-corrects.
// ─────────────────────────────────────────────────────────────────────────────

interface DriftReport {
  readonly matchId: string;
  readonly divergent: { readonly hypothesisId: string; readonly cached: number; readonly counted: number }[];
}

/**
 * Compares every `cachedCount` against a counted replay of the match's Observation ledger.
 *
 * §13.6: *"A reconcile assertion runs at every `Match.state` transition (rare, never on the tap path)
 * for the active match … Divergence is logged at `error` and surfaced in debug; it is never
 * auto-corrected during a match. This is a smoke alarm for the failure mode where a future code path
 * writes an Observation without going through the bus — the ESLint boundary is the wall, this is the
 * alarm."*
 *
 * It is **not** a postcondition and deliberately does not throw: aborting a Full Time transition
 * because a count drifted would turn a reporting mechanism into the data-loss event it exists to
 * warn about. The reading happens inside the transaction so the two sides are consistent; the log
 * line is emitted after commit, because the logger never writes inside a transaction (§5.1).
 */
async function checkDrift(matchId: string): Promise<DriftReport> {
  const rows = await hypotheses.listHypothesesForMatch(matchId);
  const ledger = await observations.listObservationsForMatch(matchId);
  const overrides = new Map(rows.map((row) => [row.id, row.statusOverride]));
  const counted = replay(ledger, overrides);

  const divergent = rows
    .map((row) => ({
      hypothesisId: row.id,
      cached: row.cachedCount,
      counted: counted.get(row.id)?.cachedCount ?? 0,
    }))
    .filter((entry) => entry.cached !== entry.counted);

  return { matchId, divergent };
}

function reportDrift(report: DriftReport | null): void {
  if (report === null || report.divergent.length === 0) return;
  log.error(MODULE, 'INV-2 drift detected: cachedCount disagrees with the observation ledger', {
    matchId: report.matchId,
    divergent: report.divergent,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// OBS-1
// ─────────────────────────────────────────────────────────────────────────────

/** Emitted **after** the transaction settles — §5.1: the logger never writes inside one. */
function emit(lines: readonly DomainLogEntry[]): void {
  for (const entry of lines) {
    log[entry.level](entry.module, entry.msg, { ...entry.data, matchId: entry.matchId });
  }
}

export type { Effect };
